import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { config, OAUTH_CALLBACK_PATH } from '../config/env';
import { setSessionExpiry } from '../utils/session';
import {
  getAndClearPkceVerifier,
  getAndClearOAuthState,
  getAndClearRedirectPath,
} from '../utils/pkce';
import { LoadingDisplay } from '../components/LoadingDisplay';
import { ErrorDisplay } from '../components/ErrorDisplay';

interface CallbackResponse {
  // Blanked by the server (SEC-128): the token arrives as an HttpOnly cookie.
  access_token?: string;
  token_type: string;
  expires_in: number;
}

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      // Check for OAuth error in URL
      const errorParam = searchParams.get('error');
      if (errorParam) {
        const errorDescription =
          searchParams.get('error_description') || 'Unknown OAuth error';
        setError(`${errorParam}: ${errorDescription}`);
        return;
      }

      // Get authorization code
      const code = searchParams.get('code');
      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Verify state matches
      const returnedState = searchParams.get('state');
      const storedState = getAndClearOAuthState();
      if (!storedState || returnedState !== storedState) {
        setError('Invalid OAuth state - possible CSRF attack');
        return;
      }

      // Get PKCE verifier
      const codeVerifier = getAndClearPkceVerifier();
      if (!codeVerifier) {
        setError('Missing PKCE verifier - please try logging in again');
        return;
      }

      // Build redirect URI (must match what was sent in authorization request)
      const redirectUri = new URL(OAUTH_CALLBACK_PATH, window.location.origin)
        .href;

      try {
        // Exchange code for tokens via API
        const response = await fetch(`${config.apiBaseUrl}/auth/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'include', // Include cookies for the refresh token
          body: JSON.stringify({
            code,
            code_verifier: codeVerifier,
            redirect_uri: redirectUri,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Token exchange failed: ${errorText}`);
        }

        const data: CallbackResponse = await response.json();

        // The token is set as an HttpOnly cookie by the server; record only its
        // expiry hint so the client can refresh proactively.
        const nowSec = Math.floor(Date.now() / 1000);
        setSessionExpiry(nowSec + data.expires_in);

        // Redirect to original page or default
        const redirectPath = getAndClearRedirectPath() || '/';
        navigate(redirectPath, { replace: true });
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to complete login'
        );
      }
    }

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <ErrorDisplay message={`Authentication Error: ${error}`} />
        <button
          onClick={() => navigate('/', { replace: true })}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Go to Home
        </button>
      </div>
    );
  }

  return (
    <LoadingDisplay message="Completing login..." subtitle="Please wait" />
  );
}
